import { useState } from 'react';

import type {
  MissedCommentDialogMode,
  MissedCommentDialogProps,
} from './MissedCommentDialog';

import {
  useMarkMissedBookingReservationMutation,
  useUpdateMissedCommentBookingReservationMutation,
} from '@/mutations';

import { notifyBookingSuccess, notifyMutationError } from '@/helpers';

type MissedCommentTarget = {
  reservationId: number;
  mode: MissedCommentDialogMode;
  comment: string | null;
};

type UseMissedCommentDialogParams = {
  reservationsUrl: string;
  onMarked: () => void;
  onEdited: () => void;
};

export const useMissedCommentDialog = (
  params: UseMissedCommentDialogParams,
) => {
  const { reservationsUrl, onMarked, onEdited } = params;

  const [target, setTarget] = useState<MissedCommentTarget | null>(null);

  const { mutate: markMissed, isPending: isMarkPending } =
    useMarkMissedBookingReservationMutation();
  const { mutate: updateMissedComment, isPending: isUpdatePending } =
    useUpdateMissedCommentBookingReservationMutation();

  const isPending = isMarkPending || isUpdatePending;

  const onConfirm = (missedComment: string) => {
    if (!target) {
      return;
    }
    const url = `${reservationsUrl}/${target.reservationId}`;

    if (target.mode === 'edit') {
      updateMissedComment(
        { url, data: { missed_comment: missedComment } },
        {
          onSuccess: (response) => {
            notifyBookingSuccess(response);
            setTarget(null);
            onEdited();
          },
          onError: notifyMutationError,
        },
      );
      return;
    }

    markMissed(
      { url, data: missedComment ? { missed_comment: missedComment } : {} },
      {
        onSuccess: (response) => {
          notifyBookingSuccess(response);
          setTarget(null);
          onMarked();
        },
        onError: (error) => {
          notifyMutationError(error);
          onEdited();
        },
      },
    );
  };

  const dialogProps: MissedCommentDialogProps | null = target && {
    mode: target.mode,
    defaultComment: target.comment,
    isPending,
    onClose: () => setTarget(null),
    onConfirm,
  };

  return {
    isPending,
    dialogProps,
    openMark: (reservationId: number) =>
      setTarget({ reservationId, mode: 'mark', comment: null }),
    openEdit: (reservationId: number, comment: string | null) =>
      setTarget({ reservationId, mode: 'edit', comment }),
  };
};
